import { useEffect, useRef, useState, type DragEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { IconUpload } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { Field, SettingsGrid } from "@/components/settings/settings-kit";
import { MediaFit, MediaPanel, useMediaRatio } from "@/components/content-programs/submission-dialog";
import ContentRequirementsPanel from "@/components/affiliate/content-programs/ContentRequirementsPanel";
import SampleContentGallery from "@/components/affiliate/content-programs/SampleContentGallery";
import ValidationFeedback from "@/components/affiliate/content-programs/ValidationFeedback";
import ProductMultiSelect from "@/components/brand/ProductMultiSelect";
import { storageService } from "@/services/storage.service";
import {
  listAffiliateEligiblePrograms,
  listAffiliateMySubmissions,
  listProgramProducts,
  resubmitAffiliateSubmission,
  submitAffiliateContent,
} from "@/services/contentProgram.service";
import {
  getAcceptMimeList,
  getUploadVibe,
  pickDefaultPreviewRatio,
  validateContentFileWithConfig,
  type ContentProgramConfig,
  type ContentValidationResult,
} from "@/config/contentProgramUpload";
import { getMyAdRequest } from "@/services/affiliateAdRequest.service";
import { AD_REQUEST_CHECKLIST } from "@/services/tiktokShop.service";
import { formatAmount } from "@/utils/formatters";
import { toGidProductIds, toNumericProductIds } from "@/utils/productIds";
import { cn } from "@/lib/utils";
import { brandLabel } from "./ContentProgramsList";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function fileSize(bytes: number) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

const CLOSE_TO = "/affiliate/content-programs";

/** Bones in the shape of the form and its aside. */
function PageSkeleton() {
  return (
    <SettingsGrid
      form={
        <div className="space-y-4 animate-pulse">
          {[3, 1].map((fields, i) => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="px-5 pb-3 pt-4">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
              <div className="space-y-4 px-5 pb-5">
                {Array.from({ length: fields }).map((_, j) => (
                  <div key={j} className="space-y-1.5">
                    <div className="h-3.5 w-24 rounded bg-muted" />
                    <div className="h-9 w-full rounded-lg bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      }
      aside={
        <div className="rounded-lg border border-border bg-card animate-pulse">
          <div className="px-5 pb-3 pt-4">
            <div className="h-4 w-20 rounded bg-muted" />
          </div>
          <div className="mx-5 mb-5 h-72 rounded-md bg-muted" />
        </div>
      }
    />
  );
}

/**
 * Submit creative to a program: the form on the left, and on the right the
 * preview drawn at the file's own size, the checks it must pass, and what
 * the brand accepts.
 */
export default function AffiliateSubmitContent() {
  const { programId } = useParams<{ programId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editContentId = searchParams.get("resubmit") ?? undefined;
  const adRequestId = searchParams.get("adRequest") ?? undefined;

  const [title, setTitle] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [consented, setConsented] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ContentValidationResult | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [programProducts, setProgramProducts] = useState<Array<{ id: string; title: string; image: string | null }>>([]);
  const [productsCursor, setProductsCursor] = useState<string | null>(null);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);

  const eligibleQuery = useQuery({
    queryKey: ["affiliate-eligible-content-programs"],
    queryFn: listAffiliateEligiblePrograms,
    enabled: Boolean(programId),
  });
  const mySubmissionsQuery = useQuery({
    queryKey: ["affiliate-my-content-submissions"],
    queryFn: listAffiliateMySubmissions,
    enabled: Boolean(editContentId),
  });
  const adRequestQuery = useQuery({
    queryKey: ["affiliate-ad-request", adRequestId],
    queryFn: () => getMyAdRequest(adRequestId!),
    enabled: Boolean(adRequestId),
  });
  const adRequest = adRequestQuery.data && adRequestQuery.data.status === "ACCEPTED" ? adRequestQuery.data : null;
  const programProductsQuery = useQuery({
    queryKey: ["affiliate-program-products", programId],
    queryFn: () => listProgramProducts(programId!),
    enabled: Boolean(programId),
  });

  useEffect(() => {
    const data = programProductsQuery.data;
    if (!data) return;
    setProgramProducts(data.products);
    setProductsCursor(data.pagination.endCursor);
    setHasMoreProducts(data.pagination.hasNextPage);
  }, [programProductsQuery.data]);

  useEffect(() => {
    if (!editContentId || !mySubmissionsQuery.data) return;
    const existing = mySubmissionsQuery.data.find((s) => s.id === editContentId);
    if (!existing) return;
    if (existing.title) setTitle(existing.title);
    if (existing.productIds?.length) setSelectedProductIds(toGidProductIds(existing.productIds));
  }, [editContentId, mySubmissionsQuery.data]);

  // The request names the video; start the title from its caption.
  useEffect(() => {
    if (!adRequest || editContentId || title) return;
    setTitle((adRequest.video.description || "TikTok video").slice(0, 120));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adRequest?.id]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const prog = eligibleQuery.data?.find((p) => p.id === programId);
  const pageLoading = eligibleQuery.isLoading || (Boolean(editContentId) && mySubmissionsQuery.isLoading);
  const contentConfig = (prog?.contentConfig ?? null) as ContentProgramConfig | null;
  const samples = prog?.samples ?? [];
  const uploadVibe = getUploadVibe(contentConfig?.allowedMediaTypes);
  const acceptMimes = getAcceptMimeList(contentConfig);
  const previewKind = validation && validation.ok ? validation.mediaType : null;
  const detectedRatio = validation && "aspectRatio" in validation ? validation.aspectRatio : null;
  const seedRatio = detectedRatio ?? pickDefaultPreviewRatio(contentConfig?.allowedAspectRatios);
  const { ratio, setNatural } = useMediaRatio(previewUrl, seedRatio);

  const runValidation = async (f: File) => {
    setValidating(true);
    setFileError(null);
    try {
      const result = await validateContentFileWithConfig(f, contentConfig);
      setValidation(result);
      if (!result.ok) {
        setFileError(result.error);
        setFile(null);
        return;
      }
      setFile(f);
    } catch (e) {
      setFileError(e instanceof Error ? e.message : "Could not read the file");
      setFile(null);
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const onPickFile = (f: File | null) => {
    if (!f) return;
    void runValidation(f);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    onPickFile(e.dataTransfer.files?.[0] ?? null);
  };

  const submitMut = useMutation({
    mutationFn: async () => {
      const t = title.trim();
      if (!programId || !t) throw new Error("Give the content a title");
      if (!selectedProductIds.length) throw new Error("Pick at least one product");
      if (!file || !validation?.ok) throw new Error("Choose a file that passes every check");

      const mediaUrl = await storageService.uploadContentSubmissionMedia(file);
      const body = {
        title: t,
        ...(adRequest && !editContentId ? { adRequestId: adRequest.id } : {}),
        productIds: toNumericProductIds(selectedProductIds),
        mediaUrl,
        mediaType: validation.mediaType,
        mimeType: file.type || "application/octet-stream",
        byteSize: file.size,
        width: validation.width,
        height: validation.height,
        ...(validation.durationSeconds != null ? { durationSeconds: validation.durationSeconds } : {}),
        ...(validation.aspectRatio ? { aspectRatio: validation.aspectRatio } : {}),
        ...(thumbnailUrl.trim()
          ? { thumbnailUrl: thumbnailUrl.trim() }
          : validation.mediaType === "IMAGE"
            ? { thumbnailUrl: mediaUrl }
            : {}),
      };
      return editContentId ? resubmitAffiliateSubmission(editContentId, body) : submitAffiliateContent(programId, body);
    },
    onSuccess: () => {
      toast.success(editContentId ? "Content resubmitted" : "Content submitted");
      navigate("/affiliate/content-programs/submissions");
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const loadMoreProducts = async () => {
    if (!programId || !productsCursor || loadingMoreProducts) return;
    setLoadingMoreProducts(true);
    try {
      const data = await listProgramProducts(programId, { cursor: productsCursor });
      setProgramProducts((prev) => [...prev, ...data.products]);
      setProductsCursor(data.pagination.endCursor);
      setHasMoreProducts(data.pagination.hasNextPage);
    } finally {
      setLoadingMoreProducts(false);
    }
  };

  const uploadLabel = uploadVibe === "image" ? "Image" : uploadVibe === "video" ? "Video" : "Image or video";
  const uploadHint = uploadVibe === "image" ? "JPEG, PNG, WebP or GIF" : uploadVibe === "video" ? "MP4 or MOV" : "JPEG, PNG, WebP, GIF, MP4 or MOV";
  const canSubmit = Boolean(prog && consented && file && validation?.ok && title.trim() && selectedProductIds.length) && !submitMut.isPending;

  if (!pageLoading && !prog) {
    return (
      <div className="space-y-4">
        <PageHeader title="Submit content" closeTo={CLOSE_TO} />
        <EmptyState
          title="Program not available"
          body="It may have closed, or you are no longer enrolled with this brand"
          action={
            <Button variant="outline" onClick={() => navigate(CLOSE_TO)}>
              See programs
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title={prog ? `${editContentId ? "Resubmit to " : ""}${prog.title}` : "Submit content"} closeTo={CLOSE_TO} />

      {pageLoading || !prog ? (
        <PageSkeleton />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitMut.mutate();
          }}
        >
          <SettingsGrid
            form={
              <>
                {editContentId && (
                  <DialogNotice tone="warning">
                    {brandLabel(prog.brandDetail)} asked for changes. Upload the revised file; the title and products are carried over.
                  </DialogNotice>
                )}

                {adRequest && !editContentId && (
                  <DashboardCard title={`${adRequest.brand.name} asked for this video`} bodyClassName="space-y-3 px-5 pb-5">
                    <p className="text-sm text-foreground">
                      {adRequest.video.description || "Your TikTok video"}
                      {adRequest.video.pageLink || adRequest.video.url ? (
                        <>
                          {" "}
                          <a href={adRequest.video.pageLink ?? adRequest.video.url ?? "#"} target="_blank" rel="noreferrer" className="text-muted-foreground underline-offset-4 hover:underline">
                            Open on TikTok
                          </a>
                        </>
                      ) : null}
                    </p>
                    {adRequest.checklist.length > 0 && (
                      <ul className="list-disc space-y-0.5 pl-4 text-sm text-foreground">
                        {adRequest.checklist.map((c) => (
                          <li key={c}>{AD_REQUEST_CHECKLIST.find((i) => i.value === c)?.label ?? c}</li>
                        ))}
                      </ul>
                    )}
                    {adRequest.notes && <p className="text-sm text-foreground">Edits they want: {adRequest.notes}</p>}
                    <p className="text-sm text-muted-foreground">
                      Pay:{" "}
                      {[
                        adRequest.flatFee ? `${formatAmount(adRequest.flatFee)} once approved` : null,
                        adRequest.commissionRate > 0 ? `${adRequest.commissionRate}% of the sales the ad drives` : null,
                      ]
                        .filter(Boolean)
                        .join(" + ") || "none set"}
                    </p>
                  </DashboardCard>
                )}

                <DashboardCard title="Content" bodyClassName="space-y-5 px-5 pb-5">
                  <Field label="Title" htmlFor="sc-title" help="What the brand sees first in review">
                    <Input id="sc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Morning routine reel" autoComplete="off" />
                  </Field>

                  <Field label="Products" help="The products this creative promotes">
                    <ProductMultiSelect
                      products={programProducts}
                      selectedIds={selectedProductIds}
                      onChange={setSelectedProductIds}
                      loading={programProductsQuery.isLoading}
                      required
                      onLoadMore={() => void loadMoreProducts()}
                      hasMore={hasMoreProducts}
                      loadingMore={loadingMoreProducts}
                    />
                  </Field>

                  <Field label={uploadLabel} help={uploadHint}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={onDrop}
                      disabled={validating}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
                        dragging ? "border-brand-600 bg-muted/60" : "border-border hover:bg-muted/60"
                      )}
                    >
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <IconUpload size={16} stroke={1.75} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        {file ? (
                          <>
                            <span className="block truncate text-sm font-medium text-foreground">{file.name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {fileSize(file.size)}. Choose another file to replace it
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="block text-sm font-medium text-foreground">
                              {validating ? "Checking the file" : "Drop a file here or choose one"}
                            </span>
                            <span className="block text-xs text-muted-foreground">{uploadHint}</span>
                          </>
                        )}
                      </span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={acceptMimes}
                      className="hidden"
                      onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                    />
                    {fileError && <p className="text-xs text-destructive">{fileError}</p>}
                  </Field>

                  {uploadVibe !== "image" && (
                    <Field label="Thumbnail" htmlFor="sc-thumb" help="Optional. An image URL shown for a video before it plays">
                      <Input
                        id="sc-thumb"
                        value={thumbnailUrl}
                        onChange={(e) => setThumbnailUrl(e.target.value)}
                        placeholder="https://"
                        inputMode="url"
                        autoComplete="off"
                      />
                    </Field>
                  )}
                </DashboardCard>

                <DashboardCard title="Usage rights" bodyClassName="px-5 pb-5">
                  <label htmlFor="sc-consent" className="flex cursor-pointer items-start gap-3">
                    <Checkbox id="sc-consent" checked={consented} onCheckedChange={(v) => setConsented(v === true)} className="mt-0.5" />
                    <span className="text-sm text-foreground">
                      This creative may be edited, published and run as paid advertising by Reveshare and {brandLabel(prog.brandDetail)}
                      {" "}on Meta and other platforms, as set out in the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium underline-offset-4 hover:underline">
                        terms
                      </a>
                      .
                    </span>
                  </label>
                </DashboardCard>

                <div className="flex items-center justify-end gap-4 rounded-lg border border-border bg-card px-5 py-3">
                  <DialogTextButton onClick={() => navigate(CLOSE_TO)} disabled={submitMut.isPending}>
                    Cancel
                  </DialogTextButton>
                  <Button type="submit" disabled={!canSubmit}>
                    {submitMut.isPending ? "Uploading" : editContentId ? "Resubmit for review" : "Submit for review"}
                  </Button>
                </div>
              </>
            }
            aside={
              <>
                <DashboardCard title="Preview" bodyClassName="px-5 pb-5">
                  <MediaPanel className="h-[22rem] rounded-md p-3 lg:p-3">
                    <MediaFit
                      mediaUrl={previewUrl}
                      mediaType={previewKind}
                      ratio={ratio}
                      onRatio={setNatural}
                      placeholder={file ? "Checking the file" : `Your ${uploadLabel.toLowerCase()} shows here at its own size`}
                    />
                  </MediaPanel>
                  <div className="mt-3 truncate text-sm font-medium text-foreground">{title.trim() || "Untitled"}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {prog.title} for {brandLabel(prog.brandDetail)}, {prog.commissionRate}% commission
                  </div>
                  {(validating || (validation?.checks.length ?? 0) > 0) && (
                    <div className="mt-3 border-t border-border pt-1">
                      <ValidationFeedback checks={validation?.checks ?? []} validating={validating} />
                    </div>
                  )}
                </DashboardCard>

                <DashboardCard title="What the brand accepts" bodyClassName="px-5 pb-3">
                  <ContentRequirementsPanel config={contentConfig} />
                </DashboardCard>

                {samples.length > 0 && (
                  <DashboardCard title="Examples from the brand" bodyClassName="px-5 pb-5">
                    <SampleContentGallery samples={samples} />
                  </DashboardCard>
                )}
              </>
            }
          />
        </form>
      )}
    </div>
  );
}
